import React, { useState, useEffect } from 'react';
import '../Dashboard.css';
import apiClient from '../services/api';
import SettingsStations from './SettingsStations';

const Settings = ({ lang, theme, initialTab = 'general' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [settings, setSettings] = useState({});
  const [apiStatus, setApiStatus] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const i18n = {
    lo: {
      settings: 'ຕັ້ງຄ່າ',
      stations: 'ສະຖານີ',
      auditLog: 'ບັນທຶກ Audit',
      apiStatus: 'ສະຖານະ API',
      general: 'ທົ່ວໄປ',
      notification: 'ແຈ້ງເຕືອນ',
      security: 'ຄວາມປອດໄພ',
      integration: 'ເຊື່ອມຕໍ່',
      save: 'ບັນທຶກ',
      service: 'ບໍລິການ',
      status: 'ສະຖານະ',
      responseTime: 'ເວລາຕອບສະໜອງ',
      lastCheck: 'ກວດສອບຄັ້ງສຸດທ້າຍ',
      checkNow: 'ກວດສອບດຽວນີ້',
      action: 'ການກະທຳ',
      timestamp: 'ເວລາ',
      user: 'ຜູ້ໃຊ້',
      details: 'ລາຍລະອຽດ',
    },
    th: {
      settings: 'การตั้งค่า',
      stations: 'สถานี',
      auditLog: 'บันทึก Audit',
      apiStatus: 'สถานะ API',
      general: 'ทั่วไป',
      notification: 'การแจ้งเตือน',
      security: 'ความปลอดภัย',
      integration: 'การเชื่อมต่อ',
      save: 'บันทึก',
      service: 'บริการ',
      status: 'สถานะ',
      responseTime: 'เวลาตอบสนอง',
      lastCheck: 'ตรวจสอบครั้งสุดท้าย',
      checkNow: 'ตรวจสอบเดี๋ยวนี้',
      action: 'การกระทำ',
      timestamp: 'เวลา',
      user: 'ผู้ใช้',
      details: 'รายละเอียด',
    },
    en: {
      settings: 'Settings',
      stations: 'Stations',
      auditLog: 'Audit Log',
      apiStatus: 'API Status',
      general: 'General',
      notification: 'Notification',
      security: 'Security',
      integration: 'Integration',
      save: 'Save',
      service: 'Service',
      status: 'Status',
      responseTime: 'Response Time',
      lastCheck: 'Last Check',
      checkNow: 'Check Now',
      action: 'Action',
      timestamp: 'Timestamp',
      user: 'User',
      details: 'Details',
    },
  };

  const t = (key) => i18n[lang][key] || key;

  useEffect(() => {
    fetchSettings();
    fetchApiStatus();
    fetchAuditLogs();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.settingsGet();
      if (response.success && response.data) {
        setSettings(response.data);
      } else {
        // Set default mock data if no data
        setSettings({
          general: [
            { key: 'system_name', name_en: 'System Name', description: 'Name of the system', value: 'Road Fund Central', type: 'STRING', isEditable: true },
            { key: 'default_language', name_en: 'Default Language', description: 'Default language', value: 'lo', type: 'STRING', isEditable: true },
            { key: 'timezone', name_en: 'Timezone', description: 'System timezone', value: 'Asia/Vientiane', type: 'STRING', isEditable: true },
          ],
          notification: [
            { key: 'email_notifications', name_en: 'Email Notifications', description: 'Enable email notifications', value: 'true', type: 'BOOLEAN', isEditable: true },
            { key: 'sms_notifications', name_en: 'SMS Notifications', description: 'Enable SMS notifications', value: 'false', type: 'BOOLEAN', isEditable: true },
          ],
          security: [
            { key: 'session_timeout', name_en: 'Session Timeout', description: 'Session timeout in seconds', value: '3600', type: 'NUMBER', isEditable: true },
            { key: 'max_login_attempts', name_en: 'Max Login Attempts', description: 'Maximum login attempts', value: '5', type: 'NUMBER', isEditable: true },
          ],
        });
      }
    } catch (error) {
      console.error('Fetch settings error:', error);
      // Set default mock data on error
      setSettings({
        general: [
          { key: 'system_name', name_en: 'System Name', description: 'Name of the system', value: 'Road Fund Central', type: 'STRING', isEditable: true },
          { key: 'default_language', name_en: 'Default Language', description: 'Default language', value: 'lo', type: 'STRING', isEditable: true },
          { key: 'timezone', name_en: 'Timezone', description: 'System timezone', value: 'Asia/Vientiane', type: 'STRING', isEditable: true },
        ],
        notification: [
          { key: 'email_notifications', name_en: 'Email Notifications', description: 'Enable email notifications', value: 'true', type: 'BOOLEAN', isEditable: true },
          { key: 'sms_notifications', name_en: 'SMS Notifications', description: 'Enable SMS notifications', value: 'false', type: 'BOOLEAN', isEditable: true },
        ],
        security: [
          { key: 'session_timeout', name_en: 'Session Timeout', description: 'Session timeout in seconds', value: '3600', type: 'NUMBER', isEditable: true },
          { key: 'max_login_attempts', name_en: 'Max Login Attempts', description: 'Maximum login attempts', value: '5', type: 'NUMBER', isEditable: true },
        ],
      });
    }
    setLoading(false);
  };

  const fetchApiStatus = async () => {
    try {
      const response = await apiClient.settingsGetApiStatus();
      if (response.success && Array.isArray(response.data)) {
        setApiStatus(response.data);
      } else if (response.success && response.data) {
        // If data is an object, convert to array
        setApiStatus([response.data]);
      } else {
        // Set default mock data if no data
        setApiStatus([
          { name: 'smart_gate_prod', status: 'OK', responseTime: 45, lastCheck: new Date().toISOString() },
          { name: 'bridge_sys', status: 'OK', responseTime: 52, lastCheck: new Date().toISOString() },
          { name: 'wim_system', status: 'LAG', responseTime: 850, lastCheck: new Date().toISOString() },
          { name: 'auction_svc', status: 'OK', responseTime: 38, lastCheck: new Date().toISOString() },
        ]);
      }
    } catch (error) {
      console.error('Fetch API status error:', error);
      // Set default mock data on error
      setApiStatus([
        { name: 'smart_gate_prod', status: 'OK', responseTime: 45, lastCheck: new Date().toISOString() },
        { name: 'bridge_sys', status: 'OK', responseTime: 52, lastCheck: new Date().toISOString() },
        { name: 'wim_system', status: 'LAG', responseTime: 850, lastCheck: new Date().toISOString() },
        { name: 'auction_svc', status: 'OK', responseTime: 38, lastCheck: new Date().toISOString() },
      ]);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await apiClient.settingsGetAuditLogs({ limit: 50 });
      if (response.success && Array.isArray(response.data)) {
        setAuditLogs(response.data);
      } else {
        // Set default mock data if no data
        setAuditLogs([
          { id: 1, timestamp: new Date().toISOString(), action: 'LOGIN', user_id: 'admin', resource_type: 'User', resource_id: '1' },
          { id: 2, timestamp: new Date(Date.now() - 3600000).toISOString(), action: 'UPDATE_SETTINGS', user_id: 'admin', resource_type: 'Settings', resource_id: 'system_name' },
          { id: 3, timestamp: new Date(Date.now() - 7200000).toISOString(), action: 'EXPORT_REPORT', user_id: 'manager1', resource_type: 'Report', resource_id: 'daily_2026_03_18' },
        ]);
      }
    } catch (error) {
      console.error('Fetch audit logs error:', error);
      // Set default mock data on error
      setAuditLogs([
        { id: 1, timestamp: new Date().toISOString(), action: 'LOGIN', user_id: 'admin', resource_type: 'User', resource_id: '1' },
        { id: 2, timestamp: new Date(Date.now() - 3600000).toISOString(), action: 'UPDATE_SETTINGS', user_id: 'admin', resource_type: 'Settings', resource_id: 'system_name' },
        { id: 3, timestamp: new Date(Date.now() - 7200000).toISOString(), action: 'EXPORT_REPORT', user_id: 'manager1', resource_type: 'Report', resource_id: 'daily_2026_03_18' },
      ]);
    }
  };

  const handleCheckApi = async (serviceName) => {
    try {
      await apiClient.settingsCheckApiStatus(serviceName);
      fetchApiStatus();
    } catch (error) {
      console.error('Check API error:', error);
    }
  };

  return (
    <div className="main">
      <div className="section-header">
        <div>
          <div className="section-title">{t('settings')}</div>
          <div className="section-sub">{lang === 'lo' ? 'ຈັດການລະບົບ' : lang === 'th' ? 'จัดการระบบ' : 'System Management'}</div>
        </div>
      </div>

      <div className="tabs">
        <div className={`tab ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>
          ⚙ {t('settings')}
        </div>
        <div className={`tab ${activeTab === 'stations' ? 'active' : ''}`} onClick={() => setActiveTab('stations')}>
          🏢 {t('stations')}
        </div>
        <div className={`tab ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>
          ◎ {t('auditLog')}
        </div>
        <div className={`tab ${activeTab === 'api' ? 'active' : ''}`} onClick={() => setActiveTab('api')}>
          ⬡ {t('apiStatus')}
        </div>
      </div>

      {activeTab === 'general' && (
        <div className="card">
          <div className="card-title">{t('settings')}</div>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text2)' }}>Loading...</div>
          ) : (
            <div style={{ marginTop: '20px' }}>
              {Object.keys(settings).map((category) => (
                <div key={category} style={{ marginBottom: '30px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '15px', color: 'var(--text)' }}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </h3>
                  <div style={{ display: 'grid', gap: '15px' }}>
                    {Array.isArray(settings[category]) && settings[category].map((setting) => (
                      <div
                        key={setting.key}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '200px 1fr',
                          gap: '20px',
                          padding: '12px',
                          background: 'var(--bg3)',
                          borderRadius: '6px',
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '13px' }}>{setting.name_en}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{setting.description}</div>
                        </div>
                        <div>
                          {setting.type === 'BOOLEAN' ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={setting.value === 'true'}
                                disabled={!setting.isEditable}
                                style={{ width: '18px', height: '18px' }}
                              />
                              <span style={{ fontSize: '12px' }}>{setting.value === 'true' ? 'Enabled' : 'Disabled'}</span>
                            </label>
                          ) : setting.type === 'NUMBER' ? (
                            <input
                              type="number"
                              value={setting.value}
                              disabled={!setting.isEditable}
                              style={{
                                padding: '6px 10px',
                                borderRadius: '4px',
                                border: '1px solid var(--border)',
                                background: 'var(--bg2)',
                                color: 'var(--text)',
                                width: '150px',
                              }}
                            />
                          ) : (
                            <input
                              type="text"
                              value={setting.value}
                              disabled={!setting.isEditable}
                              style={{
                                padding: '6px 10px',
                                borderRadius: '4px',
                                border: '1px solid var(--border)',
                                background: 'var(--bg2)',
                                color: 'var(--text)',
                                width: '100%',
                                maxWidth: '300px',
                              }}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <button
                style={{
                  marginTop: '20px',
                  padding: '10px 30px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#1890ff',
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: 'var(--font)',
                }}
              >
                {t('save')}
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'stations' && (
        <SettingsStations lang={lang} />
      )}

      {activeTab === 'audit' && (
        <div className="card">
          <div className="card-title">{t('auditLog')}</div>
          <table className="data-table" style={{ marginTop: '20px' }}>
            <thead>
              <tr>
                <th>{t('timestamp')}</th>
                <th>{t('action')}</th>
                <th>{t('user')}</th>
                <th>{t('details')}</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(auditLogs) && auditLogs.length > 0 ? (
                auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '11px' }}>
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          background: 'rgba(24,144,255,.15)',
                          color: '#1890ff',
                        }}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td>{log.user_id || 'System'}</td>
                    <td style={{ fontSize: '11px', color: 'var(--text3)' }}>
                      {log.resource_type}: {log.resource_id}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: 'var(--text3)' }}>
                    No audit logs available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'api' && (
        <div className="card">
          <div className="card-title">{t('apiStatus')}</div>
          <div style={{ marginTop: '20px', display: 'grid', gap: '12px' }}>
            {Array.isArray(apiStatus) && apiStatus.length > 0 ? (
              apiStatus.map((service) => (
                <div
                  key={service.name}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '200px 100px 150px 200px 1fr',
                    gap: '15px',
                    padding: '15px',
                    background: 'var(--bg3)',
                    borderRadius: '8px',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '500', fontSize: '13px' }}>{service.name}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                      {service.name}
                    </div>
                  </div>
                  <div>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500',
                        background: service.status === 'OK' ? 'rgba(82,196,26,.15)' : 'rgba(250,140,22,.15)',
                        color: service.status === 'OK' ? '#52c41a' : '#fa8c16',
                      }}
                    >
                      {service.status === 'OK' ? '✓ OK' : '⚠ LAG'}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text2)' }}>
                    {service.responseTime}ms
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                    {service.lastCheck ? new Date(service.lastCheck).toLocaleString() : '-'}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => handleCheckApi(service.name)}
                      style={{
                        padding: '6px 15px',
                        borderRadius: '4px',
                        border: '1px solid var(--border)',
                        background: 'var(--bg2)',
                        color: 'var(--text)',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontFamily: 'var(--font)',
                      }}
                    >
                      {t('checkNow')}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)' }}>
                No API services available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
